import { useReduxState } from 'hooks';
import { getHomePageModulesByType } from 'lib/homepage';
import {
  HomePageSettingsModulesCheckErrors,
  useHomePageSettingsContext
} from '.';

import type { ModuleInSettings } from './HomePageSettingsModules';
import { Switch } from 'components/common/form';

interface Props {
  index: number;
  module: ModuleInSettings;
  updateModule: (index: number, module: ModuleInSettings) => void;
}

const HomePageSettingsModulesModule = ({
  index,
  module,
  updateModule
}: Props) => {
  const { errors } = useHomePageSettingsContext();

  const { language, messages, serverConfig, theme } = useReduxState();

  const handleUpdate = (modifiedModule: ModuleInSettings) =>
    updateModule(index, modifiedModule);

  const modules = getHomePageModulesByType({ messages, serverConfig });

  if (!module.type) return null;

  const moduleInfo = modules[module.type];

  const SettingsComponent = moduleInfo.SettingsComponent;

  const title =
    module.titleTranslations?.[language] || module.title || moduleInfo.title;

  const description =
    module.descriptionTranslations?.[language] || module.description;

  const hasErrors = errors[index];

  return (
    <div className="module">
      <div className="title-container">
        <div className="title">{title}</div>

        {['active-courses', 'active-paths'].includes(module.type) ? (
          <Switch
            {...(module.type === 'active-courses'
              ? {
                  field: 'homePageShowMyCourses',
                  id: 'home-page-show-my-courses',
                  labelBefore: messages.homePageSettingsShowMyCourses
                }
              : {
                  field: 'homePageShowMyPaths',
                  id: 'home-page-show-my-paths',
                  labelBefore: messages.homePageSettingsShowMyPaths
                })}
          />
        ) : null}
      </div>

      {module.type === 'active-courses' || module.type === 'active-paths' ? (
        <div className="active">{moduleInfo.blurb}</div>
      ) : (
        <>
          {description ? (
            <div className="description">{description}</div>
          ) : null}

          <SettingsComponent module={module} updateModule={handleUpdate} />

          <HomePageSettingsModulesCheckErrors index={index} module={module} />
        </>
      )}

      <style jsx>{`
        .module {
          background-color: ${theme.lightGreyLighter};
          border-radius: 10px;
          border: ${hasErrors ? `2px dashed ${theme.dangerColor}` : ''};
          flex-grow: 1;
        }
        .title-container {
          align-items: center;
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          justify-content: space-between;
          padding: 20px 20px 0;
        }
        .title {
          color: ${theme.textColor};
          font-size: 1.6rem;
          font-weight: 700;
        }
        .description {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          font-weight: 400;
          line-height: 1.4;
          padding: 5px 20px 0;
        }
        .content {
          margin-top: 10px;
        }
        .active {
          align-items: center;
          background-color: white;
          border-radius: 6px;
          color: ${theme.textColorLight};
          display: flex;
          font-size: 1.5rem;
          justify-content: center;
          margin: 20px 20px;
          padding: 15px;
        }
      `}</style>
    </div>
  );
};

export default HomePageSettingsModulesModule;
